import * as z from "zod";

export const SupplierStatusEnum = z.enum([
  "active",
  "inactive",
  "pending_approval",
  "suspended",
]);

export const SupplierServiceTypeEnum = z.enum([
  "supply",
  "fabrication",
  "coating",
  "assembly",
  "modification",
]);

export const SupplierSchema = z.object({
  id: z.string().uuid(),
  company_id: z.string().uuid(),
  type: z.string().optional(), // Can store multiple types as comma-separated or JSON
  supplier_code: z
    .string()
    .max(100, "Supplier code must be less than 100 characters")
    .optional(),
  status: SupplierStatusEnum.default("active"),
  notes: z.string().optional(),
  created: z
    .date()
    .optional()
    .default(() => new Date()),
  updated: z
    .date()
    .optional()
    .default(() => new Date()),
});

export const createSupplierSchema = SupplierSchema.omit({
  id: true,
  created: true,
  updated: true,
});

export const updateSupplierSchema = SupplierSchema.partial().omit({
  id: true,
  created: true,
  updated: true,
});

// Enhanced relationship schemas
export const SupplierComponentSchema = z.object({
  id: z.string().uuid().optional(),
  supplier_id: z.string().uuid(),
  component_id: z.string().uuid(),
  service_type: SupplierServiceTypeEnum.default("supply"),
  supplier_part_number: z
    .string()
    .max(100, "Supplier part number must be less than 100 characters")
    .optional(),
  unit_price: z
    .number()
    .min(0, "Unit price cannot be negative")
    .multipleOf(0.01, "Unit price must have at most 2 decimal places")
    .optional(),
  currency: z
    .string()
    .length(3, "Currency must be 3 characters")
    .default("USD"),
  minimum_quantity: z
    .number()
    .int("Minimum quantity must be a whole number")
    .min(0, "Minimum quantity cannot be negative")
    .optional(),
  lead_time_days: z
    .number()
    .int("Lead time days must be a whole number")
    .min(0, "Lead time days cannot be negative")
    .optional(),
  is_preferred: z.boolean().default(false),
  last_order_date: z.date().optional(),
  total_orders: z.number().int().min(0).default(0),
  notes: z.string().optional(),
});

export const SupplierProductSchema = z.object({
  id: z.string().uuid().optional(),
  supplier_id: z.string().uuid(),
  product_id: z.string().uuid(),
  service_type: SupplierServiceTypeEnum.default("supply"),
  supplier_part_number: z
    .string()
    .max(100, "Supplier part number must be less than 100 characters")
    .optional(),
  unit_price: z
    .number()
    .min(0, "Unit price cannot be negative")
    .multipleOf(0.01, "Unit price must have at most 2 decimal places")
    .optional(),
  currency: z
    .string()
    .length(3, "Currency must be 3 characters")
    .default("USD"),
  minimum_quantity: z
    .number()
    .int("Minimum quantity must be a whole number")
    .min(0, "Minimum quantity cannot be negative")
    .optional(),
  lead_time_days: z
    .number()
    .int("Lead time days must be a whole number")
    .min(0, "Lead time days cannot be negative")
    .optional(),
  is_preferred: z.boolean().default(false),
  last_order_date: z.date().optional(),
  total_orders: z.number().int().min(0).default(0),
  notes: z.string().optional(),
});

export const SupplierMaterialSchema = z.object({
  id: z.string().uuid().optional(),
  supplier_id: z.string().uuid(),
  material_id: z.string().uuid(),
  service_type: SupplierServiceTypeEnum.default("supply"),
  supplier_part_number: z
    .string()
    .max(100, "Supplier part number must be less than 100 characters")
    .optional(),
  unit_price: z
    .number()
    .min(0, "Unit price cannot be negative")
    .multipleOf(0.01, "Unit price must have at most 2 decimal places")
    .optional(),
  currency: z
    .string()
    .length(3, "Currency must be 3 characters")
    .default("USD"),
  minimum_quantity: z
    .number()
    .int("Minimum quantity must be a whole number")
    .min(0, "Minimum quantity cannot be negative")
    .optional(),
  lead_time_days: z
    .number()
    .int("Lead time days must be a whole number")
    .min(0, "Lead time days cannot be negative")
    .optional(),
  is_preferred: z.boolean().default(false),
  last_order_date: z.date().optional(),
  total_orders: z.number().int().min(0).default(0),
  // Material-specific fields
  roll_width: z
    .number()
    .min(0, "Roll width cannot be negative")
    .multipleOf(0.001, "Roll width must have at most 3 decimal places")
    .optional(),
  roll_length: z
    .number()
    .min(0, "Roll length cannot be negative")
    .multipleOf(0.001, "Roll length must have at most 3 decimal places")
    .optional(),
  sheet_width: z
    .number()
    .min(0, "Sheet width cannot be negative")
    .multipleOf(0.001, "Sheet width must have at most 3 decimal places")
    .optional(),
  sheet_height: z
    .number()
    .min(0, "Sheet height cannot be negative")
    .multipleOf(0.001, "Sheet height must have at most 3 decimal places")
    .optional(),
  thickness: z
    .number()
    .min(0, "Thickness cannot be negative")
    .multipleOf(0.001, "Thickness must have at most 3 decimal places")
    .optional(),
  notes: z.string().optional(),
});

// Create schemas (omit id, timestamps, and computed fields)
export const createSupplierComponentSchema = SupplierComponentSchema.omit({
  id: true,
  last_order_date: true,
  total_orders: true,
});

export const createSupplierProductSchema = SupplierProductSchema.omit({
  id: true,
  last_order_date: true,
  total_orders: true,
});

export const createSupplierMaterialSchema = SupplierMaterialSchema.omit({
  id: true,
  last_order_date: true,
  total_orders: true,
});

// Update schemas (make most fields optional except IDs)
export const updateSupplierComponentSchema =
  SupplierComponentSchema.partial().omit({
    id: true,
    supplier_id: true,
    component_id: true,
  });

export const updateSupplierProductSchema = SupplierProductSchema.partial().omit(
  {
    id: true,
    supplier_id: true,
    product_id: true,
  }
);

export const updateSupplierMaterialSchema =
  SupplierMaterialSchema.partial().omit({
    id: true,
    supplier_id: true,
    material_id: true,
  });

// Type exports
export type CreateSupplierInput = z.infer<typeof createSupplierSchema>;
export type UpdateSupplierInput = z.infer<typeof updateSupplierSchema>;

export type CreateSupplierComponentInput = z.infer<
  typeof createSupplierComponentSchema
>;
export type UpdateSupplierComponentInput = z.infer<
  typeof updateSupplierComponentSchema
>;

export type CreateSupplierProductInput = z.infer<
  typeof createSupplierProductSchema
>;
export type UpdateSupplierProductInput = z.infer<
  typeof updateSupplierProductSchema
>;

export type CreateSupplierMaterialInput = z.infer<
  typeof createSupplierMaterialSchema
>;
export type UpdateSupplierMaterialInput = z.infer<
  typeof updateSupplierMaterialSchema
>;
